import React from 'react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Shield, Clock, FileText, Users, ArrowRight, CheckCircle, Lock } from 'lucide-react';
interface LandingPageProps {
  onNavigate: (page: string) => void;
}
export function LandingPage({
  onNavigate
}: LandingPageProps) {
  return <div className="min-h-screen bg-white font-sans text-text-primary" data-id="element-600">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100" data-id="element-601">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-602">
          <div className="flex justify-between items-center h-16" data-id="element-603">
            <div className="flex items-center" data-id="element-604">
              <img src="https://cdn.magicpatterns.com/uploads/cF6vjSgtA8zA3PrUGfuAMk/KB_Signature_(1).png" alt="Zapiis" className="h-8" data-id="element-605" />
            </div>
            <div className="hidden md:flex items-center space-x-8" data-id="element-606">
              <a href="#features" className="text-gray-600 hover:text-primary-purple transition-colors" data-id="element-607">
                Features
              </a>
              <a href="#how-it-works" className="text-gray-600 hover:text-primary-purple transition-colors" data-id="element-608">
                How It Works
              </a>
              <Button onClick={() => onNavigate('login')} data-id="element-609">Beta Login</Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden" data-id="element-610">
        <div className="absolute inset-0 bg-primary-gradient opacity-5" data-id="element-611"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-612">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-613">
            <span className="inline-block py-1 px-3 rounded-full bg-purple-100 text-purple-700 text-sm font-semibold mb-6" data-id="element-614">
              🚀 Currently in Beta Testing
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 tracking-tight" data-id="element-615">
              Document Safely.{' '}
              <span className="text-transparent bg-clip-text bg-primary-gradient" data-id="element-616">
                Build Your Case. Get Out.
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-10 leading-relaxed" data-id="element-617">
              Timestamped proof that can't be found on your phone. Document
              what's happening as it happens—without the fear, without
              forgetting, without the overwhelm. When you're ready to take the
              next step, your case is already built.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-618">
              <Button size="lg" onClick={() => document.getElementById('waitlist')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-619">
                Join the Waitlist
              </Button>
              <Button size="lg" variant="secondary" onClick={() => onNavigate('login')} data-id="element-620">
                Start Documenting Today
              </Button>
            </div>
          </div>

          {/* Stats Card */}
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl border border-gray-100 p-8 transform translate-y-8" data-id="element-621">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-gray-100" data-id="element-622">
              <div data-id="element-623">
                <p className="text-3xl font-bold text-primary-purple mb-1" data-id="element-624">
                  100%
                </p>
                <p className="text-sm text-gray-500" data-id="element-625">Timestamped</p>
              </div>
              <div data-id="element-626">
                <p className="text-3xl font-bold text-primary-purple mb-1" data-id="element-627">
                  Web
                </p>
                <p className="text-sm text-gray-500" data-id="element-628">Access Anywhere</p>
              </div>
              <div data-id="element-629">
                <p className="text-3xl font-bold text-gray-400 mb-1" data-id="element-630">HIPAA</p>
                <p className="text-sm text-gray-400" data-id="element-631">Coming Soon</p>
              </div>
              <div data-id="element-632">
                <p className="text-3xl font-bold text-gray-400 mb-1" data-id="element-633">Cloud</p>
                <p className="text-sm text-gray-400" data-id="element-634">Sync Soon</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problems We Solve */}
      <section className="py-24 bg-gray-50" data-id="element-635">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-636">
          <div className="text-center mb-16" data-id="element-637">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-638">
              Your Biggest Worries, Solved
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-639">
            <div className="text-center" data-id="element-640">
              <div className="text-5xl mb-4" data-id="element-641">🔒</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-642">
                Fear of Discovery
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-643">
                "They'll find it on my phone"
              </p>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200" data-id="element-644">
                <p className="text-green-800 font-medium" data-id="element-645">
                  Nothing stored on your device
                </p>
              </div>
            </div>

            <div className="text-center" data-id="element-646">
              <div className="text-5xl mb-4" data-id="element-647">⏰</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-648">
                Proving It Happened
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-649">"How do I prove the date?"</p>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200" data-id="element-650">
                <p className="text-green-800 font-medium" data-id="element-651">
                  Automatic legal timestamps
                </p>
              </div>
            </div>

            <div className="text-center" data-id="element-652">
              <div className="text-5xl mb-4" data-id="element-653">😰</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-654">
                Feeling Overwhelmed
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-655">
                "I can't remember everything"
              </p>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200" data-id="element-656">
                <p className="text-green-800 font-medium" data-id="element-657">
                  Document as you go—it's done
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Prop */}
      <section className="py-32 bg-white" data-id="element-658">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-659">
          <div className="text-center mb-20" data-id="element-660">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-661">
              From Day One to Done
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-662">
              Whether you're just starting to document or preparing to meet with
              an attorney, Zapiis gives you the clarity and tools you
              need—whenever you're ready.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto" data-id="element-663">
            {/* For Individuals */}
            <div className="bg-gray-50 rounded-xl p-10 border border-gray-200" data-id="element-664">
              <div className="flex items-start gap-4 mb-6" data-id="element-665">
                <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-666">
                  <Users className="w-6 h-6 text-white" data-id="element-667" />
                </div>
                <div data-id="element-668">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-669">
                    For You
                  </h3>
                  <p className="text-sm text-gray-500 font-medium" data-id="element-670">
                    Making documenting one less thing to face alone
                  </p>
                </div>
              </div>

              <p className="text-gray-700 mb-8 leading-relaxed" data-id="element-671">
                When you're ready to take the next step, walk into that
                consultation with everything organized and ready. We're here to
                help you keep track so you can focus on taking care of yourself.
              </p>

              <ul className="space-y-4" data-id="element-672">
                {['Document incidents as they happen', 'Keep evidence organized in one place', 'See your timeline clearly', "Track how you're feeling along the way"].map(item => <li key={item} className="flex items-start gap-3 text-gray-700" data-id="element-673">
                    <div className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-2 flex-shrink-0" data-id="element-674"></div>
                    <span data-id="element-675">{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* For Attorneys */}
            <div className="bg-gray-50 rounded-xl p-10 border border-gray-200" data-id="element-676">
              <div className="flex items-start gap-4 mb-6" data-id="element-677">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-678">
                  <FileText className="w-6 h-6 text-white" data-id="element-679" />
                </div>
                <div data-id="element-680">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-681">
                    For Attorneys
                  </h3>
                  <p className="text-sm text-gray-500 font-medium" data-id="element-682">
                    Giving you the tools to help those who need them most
                  </p>
                </div>
              </div>

              <p className="text-gray-700 mb-8 leading-relaxed" data-id="element-683">
                Meet clients who arrive prepared and organized. Spend your time
                on what matters—building their case—instead of piecing together
                scattered information.
              </p>

              <ul className="space-y-4" data-id="element-684">
                {['Organized documentation from day one', 'Timestamped evidence with full context', 'Clear chronological timelines', 'Export everything when you need it'].map(item => <li key={item} className="flex items-start gap-3 text-gray-700" data-id="element-685">
                    <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0" data-id="element-686"></div>
                    <span data-id="element-687">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 bg-gray-50" data-id="element-688">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-689">
          <div className="text-center mb-16" data-id="element-690">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-691">
              Everything You Need, Nothing You Don't
            </h2>
            <p className="text-lg text-gray-600" data-id="element-692">
              Simple tools that help you stay organized without adding to your
              stress.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-693">
            {[{
            icon: <FileText className="w-8 h-8 text-purple-600" data-id="element-694" />,
            title: 'Document Incidents',
            desc: 'Keep track of what happened, when it happened'
          }, {
            icon: <Clock className="w-8 h-8 text-blue-600" data-id="element-695" />,
            title: 'See Your Timeline',
            desc: 'View everything in order, all in one place'
          }, {
            icon: <Lock className="w-8 h-8 text-green-600" data-id="element-696" />,
            title: 'Store Evidence',
            desc: 'Keep photos, videos, and documents safe'
          }, {
            icon: <Users className="w-8 h-8 text-pink-600" data-id="element-697" />,
            title: 'Track Your Wellbeing',
            desc: 'Check in with yourself along the way'
          }].map((feature, idx) => <Card key={idx} hover className="text-center" data-id="element-698">
                <div className="bg-white p-4 rounded-full shadow-sm inline-block mb-4" data-id="element-699">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-700">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-701">{feature.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 bg-white" data-id="element-702">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-703">
          <div className="text-center mb-16" data-id="element-704">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-705">
              We're Here to Help, Every Step of the Way
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8" data-id="element-706">
            {[{
            step: '1',
            title: 'Start Today',
            desc: "Create your account and begin documenting whenever you're ready."
          }, {
            step: '2',
            title: 'Keep Track',
            desc: 'Record what happens, upload evidence, and check in with yourself.'
          }, {
            step: '3',
            title: 'Stay Organized',
            desc: 'Everything stays in one place, timestamped and ready.'
          }, {
            step: '4',
            title: 'Take the Next Step',
            desc: "Export your documentation when you're ready to move forward."
          }].map((item, idx) => <div key={idx} className="text-center relative" data-id="element-707">
                <div className="w-12 h-12 bg-primary-gradient rounded-full text-white text-xl font-bold flex items-center justify-center mx-auto mb-4 shadow-lg" data-id="element-708">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-709">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-710">{item.desc}</p>
                {idx < 3 && <div className="hidden md:block absolute top-6 left-1/2 w-full h-0.5 bg-gray-100 -z-10 transform translate-x-1/2" data-id="element-711"></div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Waitlist */}
      <section id="waitlist" className="py-24 bg-primary-gradient text-white" data-id="element-712">
        <div className="max-w-3xl mx-auto px-4 text-center" data-id="element-713">
          <h2 className="text-3xl font-bold mb-6" data-id="element-714">Join the Waitlist</h2>
          <p className="text-xl text-white/90 mb-10" data-id="element-715">
            Be the first to know when Zapiis launches with full HIPAA
            compliance, cloud sync, and mobile apps. We're building tools for
            those who need them most.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto" data-id="element-716">
            <input type="email" placeholder="Enter your email address" className="flex-1 px-6 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-300" data-id="element-717" />
            <button className="bg-white text-primary-purple px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors" data-id="element-718">
              Join Now
            </button>
          </div>
          <p className="text-sm text-white/70 mt-4" data-id="element-719">
            No spam. Unsubscribe anytime. We respect your privacy.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12" data-id="element-720">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-721">
          <div className="grid md:grid-cols-4 gap-8 mb-8" data-id="element-722">
            <div data-id="element-723">
              <img src="https://cdn.magicpatterns.com/uploads/bnGCkdUziYDcbJiTJBf9n9/Nur_Falls.png" alt="Zapiis" className="h-16 mb-4" data-id="element-724" />
              <p className="text-sm" data-id="element-725">
                Professional legal documentation platform. Currently in beta.
              </p>
            </div>
            <div data-id="element-726">
              <h4 className="text-white font-bold mb-4" data-id="element-727">Product</h4>
              <ul className="space-y-2 text-sm" data-id="element-728">
                <li data-id="element-729">
                  <a href="#features" className="hover:text-white transition-colors" data-id="element-730">
                    Features
                  </a>
                </li>
                <li data-id="element-731">
                  <a href="#how-it-works" className="hover:text-white transition-colors" data-id="element-732">
                    How It Works
                  </a>
                </li>
                <li data-id="element-733">
                  <a href="#waitlist" className="hover:text-white transition-colors" data-id="element-734">
                    Join Waitlist
                  </a>
                </li>
              </ul>
            </div>
            <div data-id="element-735">
              <h4 className="text-white font-bold mb-4" data-id="element-736">Legal</h4>
              <ul className="space-y-2 text-sm" data-id="element-737">
                <li data-id="element-738">
                  <button onClick={() => onNavigate('terms')} className="hover:text-white transition-colors text-left" data-id="element-739">
                    Terms of Service
                  </button>
                </li>
                <li data-id="element-740">
                  <button onClick={() => onNavigate('privacy')} className="hover:text-white transition-colors text-left" data-id="element-741">
                    Privacy Policy
                  </button>
                </li>
                <li data-id="element-742">
                  <button onClick={() => onNavigate('disclaimers')} className="hover:text-white transition-colors text-left" data-id="element-743">
                    Disclaimers
                  </button>
                </li>
              </ul>
            </div>
            <div data-id="element-744">
              <h4 className="text-white font-bold mb-4" data-id="element-745">Support</h4>
              <p className="text-sm mb-2" data-id="element-746">Beta support available</p>
              <p className="text-sm" data-id="element-747">Full support launching Q2 2026</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-sm text-center" data-id="element-748">
            <p className="mb-2" data-id="element-749">
              Zapiis is a documentation tool, not legal advice. We are not
              attorneys and do not provide legal services.
            </p>
            <p data-id="element-750">© 2026 Zapiis. All rights reserved. Beta Version.</p>
          </div>
        </div>
      </footer>
    </div>;
}